import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AppContext, FeatureTableContext, UserContext } from "../../Context";
import {
    changeRightPanelTab,
    setBoatLocationWindowOpen,
    setCreateObjectOpen,
    setIsOpeningTool,
    setLayerListOpen,
    setLightCalculatorOpen,
    setMeasureToolOpen,
} from "../../store/appSlice";
import { StoreState } from "../../store/rootReducer";
import { canEditVersion } from "../../utils/versioningUtils";
import NfsButton from "../genericComponents/NfsButton/NfsButton";

import AddIcon from "../../assets/add.svg?react";
import LayerListIcon from "../../assets/layerlist.svg?react";
import LightbulbIcon from "../../assets/lightCalculation/lightbulb.svg?react";
import PinIcon from "../../assets/pin.svg?react";
import RulerIcon from "../../assets/ruler.svg?react";
import TableIcon from "../../assets/table.svg?react";

import { RightPanelTabs } from "../../constants/Panels";
import { setIsCreatingOverett } from "../../store/overettSlice";
import "./MapOverlay.css";
import { Tool } from "../../utils/arcgisUtils";
import { AppDispatch } from "../../store/storeSetup";
import { layerAllowsLightCalc } from "../LightCalculator/lightCalculationUtils";

const MapOverlay = () => {
    const dispatch = useDispatch<AppDispatch>();

    const userContext = useContext(UserContext);
    const featureTableContext = useContext(FeatureTableContext);
    const appContext = useContext(AppContext);

    const [canIEditVersion, setCanIEditVersion] = useState(false);

    const measurementToolOpen = useSelector((state: StoreState) => state.app.measureToolOpen);
    const layerListOpen = useSelector((state: StoreState) => state.app.layerListOpen);
    const lightCalculatorOpen = useSelector((state: StoreState) => state.app.lightCalculatorOpen);
    const createObjectOpen = useSelector((state: StoreState) => state.app.createObjectOpen);
    const boatLocationWindowOpen = useSelector((state: StoreState) => state.app.boatLocationWindowOpen);
    const isSectorEditorOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditorOpen);

    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const selectedFeature = appContext?.selectedFeature.value;
    const setSelectedFeature = appContext?.selectedFeature.set;

    const openLightCalculator = () => {
        dispatch(setIsOpeningTool(Tool.LIGHT_CALCULATOR));
        if (!lightCalculatorOpen.open) {
            const result = dispatch(changeRightPanelTab(RightPanelTabs.LIGHT_CALCULATOR));
            if (!result) {
                // User cancelled, don't open
                return;
            }
            if (selectedFeature && !layerAllowsLightCalc(selectedFeature)) {
                setSelectedFeature?.(null);
            }
            dispatch(setIsCreatingOverett(false));
            dispatch(setLightCalculatorOpen({ open: true, loadFeatureValues: false }));
        } else {
            const result = dispatch(changeRightPanelTab(RightPanelTabs.UNDEFINED));
            if (!result) {
                // User cancelled, don't close
                return;
            }
            dispatch(setLightCalculatorOpen({ open: false, loadFeatureValues: false }));
        }
    };

    const toggleMeasureTool = () => {
        dispatch(setIsOpeningTool(Tool.MEASURE));
        dispatch(setMeasureToolOpen(!measurementToolOpen));
    };

    const toggleLayerList = () => {
        dispatch(setLayerListOpen(!layerListOpen));
    };

    const toggleCreateObject = () => {
        dispatch(setCreateObjectOpen(!createObjectOpen));
    };

    const toggleBoatLocationWindow = () => {
        dispatch(setBoatLocationWindowOpen(!boatLocationWindowOpen));
    };

    useEffect(() => {
        if (activeVersion) {
            const user = userContext?.user.value;
            canEditVersion(activeVersion, versionsSharedWithMe ?? [], user).then((result) => {
                if (canIEditVersion !== result) {
                    setCanIEditVersion(result);
                    if (createObjectOpen && !result) {
                        dispatch(setCreateObjectOpen(false));
                        toast.error("Du har ikke tilgang til å opprette objekter i denne versjonen.", {
                            autoClose: false,
                        });
                    }
                }
            });
        }
    }, [activeVersion, versionsSharedWithMe]);

    return (
        <div className="map-overlay">
            <div className="map-overlay-top-right">
                <div className="flex-col small-gap no-esri-shadow">
                    <NfsButton round inverted shadow active={measurementToolOpen} onClick={toggleMeasureTool}>
                        <RulerIcon />
                    </NfsButton>
                    <NfsButton round inverted shadow active={layerListOpen} onClick={toggleLayerList}>
                        <LayerListIcon />
                    </NfsButton>
                    <NfsButton round inverted shadow active={lightCalculatorOpen?.open} onClick={openLightCalculator}>
                        <LightbulbIcon />
                    </NfsButton>
                    {canIEditVersion && !isSectorEditorOpen && (
                        <NfsButton round inverted shadow active={createObjectOpen} onClick={toggleCreateObject}>
                            <AddIcon />
                        </NfsButton>
                    )}
                    <NfsButton
                        round
                        inverted
                        shadow
                        active={featureTableContext?.isOpen.value}
                        onClick={() => featureTableContext?.isOpen.set(!featureTableContext?.isOpen.value)}
                    >
                        <TableIcon />
                    </NfsButton>
                    <NfsButton round inverted shadow active={boatLocationWindowOpen} onClick={toggleBoatLocationWindow}>
                        <PinIcon />
                    </NfsButton>
                </div>
            </div>
        </div>
    );
};

export default MapOverlay;
